'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { searchStreets } from '@/utils/api';
import {
  advanceAddressFocus,
  assignInputRef,
} from '@/components/ui/addressSearchFocus';
import styles from './addressSearchInput.module.css';

export default function StreetSearchInput({
  value = '',
  onChange,
  onStreetSelect = null,
  onQueryChange = null,
  onUnmatched = null, // Typed/pasted text is not a registry street
  settlementCode = null, // Required: settlement code to filter streets
  placeholder = 'Įveskite gatvę',
  className = '',
  error = false,
  displayValue = null,
  onEnterPress = null, // Optional callback when Enter key is pressed
  nextInputRef = null,
  inputRef: externalInputRef = null,
  hasStreets = undefined, // true | false | null (null = checking)
  noStreetsPlaceholder = 'Gatvė netaikoma',
  checkingPlaceholder = 'Tikrinama...',
  onActivate = null,
}) {
  const [searchTerm, setSearchTerm] = useState(displayValue || value || '');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStreet, setSelectedStreet] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const prevSettlementCodeRef = useRef(settlementCode);
  const resultsRef = useRef([]);
  resultsRef.current = results;

  // Format street display name
  const formatStreetName = street => {
    const type = street.street_type || '';
    const name = street.name || '';
    return type ? `${name} ${type}` : name;
  };

  // Initialize search term from displayValue or value
  useEffect(() => {
    if (displayValue !== null && displayValue !== undefined) {
      setSearchTerm(displayValue);
    } else if (value && !displayValue) {
      setSearchTerm(value);
    }
  }, [value, displayValue]);

  // Reset when settlement changes
  useEffect(() => {
    const prevSettlementCode = prevSettlementCodeRef.current;
    prevSettlementCodeRef.current = settlementCode;

    // Only reset if settlementCode changed from a value to null/undefined
    if (!settlementCode && prevSettlementCode) {
      setSearchTerm('');
      setSelectedStreet(null);
      setResults([]);
      setIsOpen(false);
      if (value) {
        onChange('');
      }
    }
  }, [settlementCode, value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search function
  const performSearch = useCallback(
    async term => {
      if (!term || term.trim().length < 2) {
        setResults([]);
        setIsOpen(false);
        return;
      }

      if (!settlementCode) {
        setResults([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const streets = await searchStreets(term.trim(), settlementCode);
        setResults(streets || []);
        setIsOpen(true);
        setHighlightedIndex(-1); // Reset highlight when new results arrive
      } catch (error) {
        console.error('[StreetSearchInput] Search error:', error);
        setResults([]);
        setIsOpen(false);
        setHighlightedIndex(-1);
      } finally {
        setIsLoading(false);
      }
    },
    [settlementCode]
  );

  // Handle input change with debounce
  const handleInputChange = e => {
    const newValue = e.target.value;
    setSearchTerm(newValue);

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    setSelectedStreet(null);

    // If input is cleared, reset everything
    if (!newValue || newValue.trim() === '') {
      setResults([]);
      setIsOpen(false);
      setHighlightedIndex(-1);
      onQueryChange?.('');
      onChange('');
      return;
    }

    // Typing/paste is not a registry selection until the user picks a result.
    onQueryChange?.(newValue);

    // Only search if we have a settlement code
    if (!settlementCode) {
      return;
    }

    // Debounce search
    debounceTimerRef.current = setTimeout(() => {
      performSearch(newValue);
    }, 300);
  };

  // Confirm only an exact registry match on blur. Never pick the first fuzzy
  // result — copy-paste of a non-existent street must stay unconfirmed.
  const confirmTypedValueIfMatch = useCallback(() => {
    const trimmed = searchTerm?.trim();
    if (
      !trimmed ||
      trimmed.length < 2 ||
      selectedStreet ||
      isLoading ||
      !settlementCode
    )
      return;

    const exactMatch = resultsRef.current.find(
      s => formatStreetName(s).trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (!exactMatch) {
      // Keep the typed text. Do not notify the parent again — a second
      // onQueryChange on blur clears the validation error set by Continue.
      if (!value) onUnmatched?.(trimmed);
      return;
    }

    setSelectedStreet(exactMatch);
    setSearchTerm(formatStreetName(exactMatch));
    setIsOpen(false);
    setResults([]);
    setHighlightedIndex(-1);
    onChange(exactMatch.code);
    if (onStreetSelect) onStreetSelect(exactMatch);
  }, [
    searchTerm,
    selectedStreet,
    isLoading,
    settlementCode,
    onChange,
    onStreetSelect,
    onUnmatched,
    value,
  ]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && wrapperRef.current.contains(event.target)) {
        return;
      }
      confirmTypedValueIfMatch();
      setIsOpen(false);
      if (selectedStreet) {
        setSearchTerm(formatStreetName(selectedStreet));
      } else if (!value && !searchTerm) {
        setSearchTerm('');
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [selectedStreet, value, searchTerm, confirmTypedValueIfMatch]);

  // Handle input focus
  const handleFocus = () => {
    if (onActivate) onActivate();

    // If we have results, show them
    if (results.length > 0 && settlementCode) {
      setIsOpen(true);
    }
    // If we have a search term but no results yet, trigger search
    else if (searchTerm && searchTerm.trim().length >= 2 && settlementCode) {
      performSearch(searchTerm);
    }
  };

  // Handle street selection
  const handleSelectStreet = street => {
    setSelectedStreet(street);
    // Use formatted name with street type (e.g., "Gedimino pr.")
    setSearchTerm(formatStreetName(street));
    setIsOpen(false);
    setResults([]);
    setHighlightedIndex(-1);
    // Pass the street code to the parent
    onChange(street.code);
    // Also call onStreetSelect if provided
    if (onStreetSelect) {
      onStreetSelect(street);
    }
    advanceAddressFocus({ nextInputRef, onEnterPress });
  };

  const isCheckingStreets = hasStreets === null;
  const isNoStreets = hasStreets === false;
  const isInputDisabled = !settlementCode || isNoStreets;

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <div
        className={`${styles.inputWrapper} ${error ? styles.inputError : ''} ${isLoading || isCheckingStreets ? styles.loading : ''} ${className}`}
      >
        <input
          ref={node => assignInputRef(inputRef, externalInputRef, node)}
          type='text'
          className={styles.input}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={() => setTimeout(confirmTypedValueIfMatch, 0)}
          onKeyDown={e => {
            if (e.key === 'Enter' && settlementCode) {
              e.preventDefault();
              if (isOpen && results.length > 0) {
                const indexToSelect =
                  highlightedIndex >= 0 ? highlightedIndex : 0;
                const streetToSelect = results[indexToSelect];
                if (streetToSelect) {
                  handleSelectStreet(streetToSelect);
                }
              } else if (!isOpen) {
                advanceAddressFocus({ nextInputRef, onEnterPress });
              }
            } else if (e.key === 'ArrowDown' && isOpen && results.length > 0) {
              e.preventDefault();
              setHighlightedIndex(prev =>
                prev < results.length - 1 ? prev + 1 : prev
              );
            }
            // Arrow up - navigate up in results
            else if (e.key === 'ArrowUp' && isOpen && results.length > 0) {
              e.preventDefault();
              setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
            }
            // Escape - close dropdown
            else if (e.key === 'Escape' && isOpen) {
              e.preventDefault();
              setIsOpen(false);
              setHighlightedIndex(-1);
            }
          }}
          placeholder={
            isNoStreets
              ? noStreetsPlaceholder
              : isCheckingStreets
                ? checkingPlaceholder
                : settlementCode
                  ? placeholder
                  : 'Pirmiausia pasirinkite miestą'
          }
          autoComplete='off'
          disabled={isInputDisabled}
        />
        {isLoading || isCheckingStreets ? (
          <div className={styles.iconWrapper}>
            <div className={styles.spinner}></div>
          </div>
        ) : null}
      </div>

      {isOpen && results.length > 0 && (
        <ul className={styles.resultsList}>
          {results.map((street, index) => (
            <li
              key={street.code || index}
              className={`${styles.resultItem} ${
                selectedStreet?.code === street.code ? styles.active : ''
              } ${highlightedIndex === index ? styles.highlighted : ''}`}
              onMouseDown={e => {
                // Select before input blur runs and avoid blur fallback race.
                e.preventDefault();
                handleSelectStreet(street);
              }}
              onClick={() => handleSelectStreet(street)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div className={styles.resultName}>
                {formatStreetName(street)}
              </div>
              {street.settlement_name && (
                <div className={styles.resultMunicipality}>
                  {street.settlement_name}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {isOpen &&
        !isLoading &&
        searchTerm.trim().length >= 2 &&
        results.length === 0 &&
        settlementCode && (
          <ul className={styles.resultsList}>
            <li className={styles.noResults}>
              Nerasta rezultatų &ldquo;{searchTerm}&rdquo;
            </li>
          </ul>
        )}
    </div>
  );
}
